import React, { useState, useEffect, useRef } from 'react';
import styles from '../css-isolated/consult_medication.module.css';
import API_BASE_URL from '../config';
import authFetch from '../utils/authFetch';

function ConsultMedication({ hideSystemHeader = false, formData: externalFormData, setFormData: setExternalFormData, isDarkMode = true }) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [formData, setFormData] = useState(externalFormData || {
    medications: [{ id: null, medicationName: '', dosage: '', quantity: '' }],
    diagnostics: '',
    referral: ''
  });
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const submitTimerRef = useRef(null);

  // Cleanup submit timer on unmount
  useEffect(() => {
    return () => {
      if (submitTimerRef.current) clearTimeout(submitTimerRef.current);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.profile-section')) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Sync external form data changes to local state
  useEffect(() => {
    if (externalFormData) {
      setFormData(externalFormData);
    }
  }, [externalFormData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'diagnostics' || name === 'referral') {
      const newData = {
        ...formData,
        [name]: value
      };
      setFormData(newData);
      // Sync with external state if provided
      if (setExternalFormData) {
        setExternalFormData(newData);
      }
    }
  };

  const handleMedicationChange = (index, field, value) => {
    const updatedMedications = formData.medications.map((medication, i) => 
      i === index ? { ...medication, [field]: value } : medication
    );
    const newData = { ...formData, medications: updatedMedications };
    setFormData(newData);
    // Sync with external state if provided
    if (setExternalFormData) {
      setExternalFormData(newData);
    }
  };

  const addMedication = () => {
    const newData = {
      ...formData,
      medications: [...formData.medications, { id: null, medicationName: '', dosage: '', quantity: '' }]
    };
    setFormData(newData);
    // Sync with external state if provided
    if (setExternalFormData) {
      setExternalFormData(newData);
    }
  };

  const removeMedication = (index) => {
    if (formData.medications.length > 1) {
      const updatedMedications = formData.medications.filter((_, i) => i !== index);
      const newData = { ...formData, medications: updatedMedications };
      setFormData(newData);
      // Sync with external state if provided
      if (setExternalFormData) {
        setExternalFormData(newData);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required medication fields
    const hasIncompleteMedication = formData.medications.some(med => 
      !med.medicationName || !med.dosage
    );
    
    if (hasIncompleteMedication) {
      alert('Please fill in all required fields: Medication Name and Dosage for each medication entry');
      return;
    }

    try {
      // API call to save consultation medication
      const response = await authFetch(`${API_BASE_URL}/api/consultation-medication`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await response.json();
      setSubmitSuccess(true);
      
      // Reset form after successful submission
      submitTimerRef.current = setTimeout(() => {
        setFormData({
          medications: [{ id: null, medicationName: '', dosage: '', quantity: '' }],
          diagnostics: '',
          referral: '',
          nodMdInitials: ''
        });
        setSubmitSuccess(false);
      }, 2000);
      
    } catch (error) {
      console.error('Error saving consultation medication:', error);
      alert('Error saving consultation medication. Please try again.');
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLogout = () => {
    // Add logout logic here
  };

  // Render embedded version when hideSystemHeader is true
  if (hideSystemHeader) {
    return (
      <div className={`${styles.embeddedContainer} ${!isDarkMode ? styles.lightMode : ''}`}>
        {/* Form Section - Direct content without separate header */}
        <div className={`${styles.formSection} ${styles.consultationFormSection}`}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionIcon}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25M9 16.5v.75m3-3V15M15 12v.75m-6.75-6.75h.008v.008H8.25V6ZM12 12h.008v.008H12V12Zm0 2.25h.008v.008H12V14.25Zm2.25-4.5h.008v.008H14.25V9.75Z" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            <h2 className={styles.sectionTitle}>Patient Medication Information</h2>
          </div>
          
          <form onSubmit={handleSubmit} className={styles.consultationForm}>
            {/* Medication Cards Section */}
            <div className={styles.medicationsContainer}>
              <h3 className={styles.subsectionTitle}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{display: 'inline-block', marginRight: '0.5rem', verticalAlign: 'middle'}}>
                  <path d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25M9 16.5v.75m3-3V15M15 12v.75m-6.75-6.75h.008v.008H8.25V6ZM12 12h.008v.008H12V12Zm0 2.25h.008v.008H12V14.25Zm2.25-4.5h.008v.008H14.25V9.75Z" stroke="currentColor" strokeWidth="2"/>
                </svg>
                Medications Prescribed
              </h3>
              <div className={styles.cardsGrid}>
                {formData.medications.map((medication, index) => (
                  <div key={index} className={styles.medicationCard}>
                    <div className={styles.cardHeader}>
                      <h4 className={styles.cardTitle}>Medication {index + 1}</h4>
                      {formData.medications.length > 1 && (
                        <button 
                          type="button" 
                          onClick={() => removeMedication(index)}
                          className={styles.removeButton}
                          title="Remove this medication"
                          aria-label="Remove medication"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                        </button>
                      )}
                    </div>
                    <div className={styles.cardContent}>
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Name of Medication *</label>
                        <input 
                          type="text" 
                          value={medication.medicationName} 
                          onChange={(e) => handleMedicationChange(index, 'medicationName', e.target.value)} 
                          className={styles.formInput} 
                          placeholder="Enter medication name"
                          autoComplete="off" 
                          required
                        />
                      </div>
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Dosage *</label>
                        <input 
                          type="text" 
                          value={medication.dosage} 
                          onChange={(e) => handleMedicationChange(index, 'dosage', e.target.value)} 
                          className={styles.formInput} 
                          placeholder="e.g., 500mg, 2 tablets"
                          autoComplete="off" 
                          required
                        />
                      </div>
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Quantity</label>
                        <input 
                          type="number" 
                          value={medication.quantity} 
                          onChange={(e) => handleMedicationChange(index, 'quantity', e.target.value)} 
                          className={styles.formInput} 
                          placeholder="Enter quantity prescribed"
                          autoComplete="off"
                          min="0"
                          step="1"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button 
                type="button" 
                onClick={addMedication}
                className={styles.addMedicationButton}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 5v14m-7-7h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Add Another Medication
              </button>
            </div>

            {/* Medical Assessment Section */}
            <div className={styles.additionalInfoSection}>
              <h3 className={styles.subsectionTitle}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{display: 'inline-block', marginRight: '0.5rem', verticalAlign: 'middle'}}>
                  <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Medical Assessment & Notes
              </h3>
              
              <div className={styles.medicalFieldsGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{display: 'inline-block', verticalAlign: 'middle'}}>
                      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Diagnostics
                  </label>
                  <textarea 
                    name="diagnostics" 
                    value={formData.diagnostics} 
                    onChange={handleInputChange} 
                    className={styles.formTextarea} 
                    placeholder="Enter diagnostic information and findings..."
                    rows="4"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{display: 'inline-block', verticalAlign: 'middle'}}>
                      <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Referral
                  </label>
                  <textarea 
                    name="referral" 
                    value={formData.referral} 
                    onChange={handleInputChange} 
                    className={styles.formTextarea} 
                    placeholder="Enter referral information or recommendations..."
                    rows="3"
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons - Removed per user request */}
          </form>

          {/* Success Message */}
          {submitSuccess && (
            <div className={styles.successMessage}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <p>Medication information saved successfully!</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Regular standalone version
  return (
    <div className={`${styles.consultMedicationContainer} ${!isDarkMode ? styles.lightMode : ''}`}>
      {/* System Header */}
      <header className={styles.systemHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.systemLogo}>
            <img src="/BJMPRO-CAR.png" alt="BJMP CAR Logo" className={styles.systemLogoImg} />
            <div className={styles.systemTitle}>
              <h1>MediCast: BJMP Health Monitoring System</h1>
              <p>Bureau of Jail Management and Penology</p>
            </div>
          </div>
        </div>
        
        <div className={styles.headerRight}>
          <div className={styles.currentDateTime}>
            <div className={styles.dateTime}>
              <span className={styles.date}>{new Date().toLocaleDateString()}</span>
              <span className={styles.time}>{new Date().toLocaleTimeString()}</span>
            </div>
          </div>
          
          <div className={`${styles.profileSection} profile-section`}>
            <div className={styles.profileInfo} onClick={toggleDropdown}>
              <div className={styles.profileAvatar}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <div className={styles.profileDetails}>
                <span className={styles.userName}>Administrator</span>
                <span className={styles.userRole}>System Admin</span>
              </div>
              <svg className={`${styles.dropdownArrow} ${isDropdownOpen ? styles.open : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <polyline points="6,9 12,15 18,9" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            
            {isDropdownOpen && (
              <div className={styles.dropdownMenu}>
                <div className={styles.dropdownItem}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Profile Settings
                </div>
                <div className={styles.dropdownDivider}></div>
                <div className={styles.dropdownItem} onClick={handleLogout}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" strokeWidth="2"/>
                    <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2"/>
                    <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Logout
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={styles.mainContent}>
        <div className={styles.contentWrapper}>
          {/* Page Header */}
          <div className={styles.headerContainer}>
            <div className={styles.headerCard}>
              <h1 className={styles.mainTitle}>Consultation Medication</h1>
              <p className={styles.subTitle}>Record patient medication and prescription information</p>
            </div>
          </div>

          {/* Consultation Form Section */}
          <div className={styles.formContainer}>
            <div className={`${styles.formSection} ${styles.consultationFormSection}`}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionIcon}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25M9 16.5v.75m3-3V15M15 12v.75m-6.75-6.75h.008v.008H8.25V6ZM12 12h.008v.008H12V12Zm0 2.25h.008v.008H12V14.25Zm2.25-4.5h.008v.008H14.25V9.75Z" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <h2 className={styles.sectionTitle}>Patient Medication Information</h2>
            </div>
            
            <form onSubmit={handleSubmit} className={styles.consultationForm}>
              {/* Medication Cards */}
              <div className={styles.medicationsContainer}>
                <h3 className={styles.subsectionTitle}>Medications</h3>
                <div className={styles.cardsGrid}>
                  {formData.medications.map((medication, index) => (
                    <div key={index} className={styles.medicationCard}>
                      <div className={styles.cardHeader}>
                        <h4 className={styles.cardTitle}>Medication {index + 1}</h4>
                        {formData.medications.length > 1 && (
                          <button 
                            type="button" 
                            onClick={() => removeMedication(index)}
                            className={styles.removeButton}
                            title="Remove this medication"
                            aria-label="Remove medication"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                          </button>
                        )}
                      </div>
                      <div className={styles.cardContent}>
                        <div className={styles.formGroup}>
                          <label className={styles.formLabel}>Name of Medication *</label>
                          <input 
                            type="text" 
                            value={medication.medicationName} 
                            onChange={(e) => handleMedicationChange(index, 'medicationName', e.target.value)} 
                            className={styles.formInput} 
                            placeholder="Enter medication name"
                            autoComplete="off" 
                            required
                          />
                        </div>
                        <div className={styles.formGroup}>
                          <label className={styles.formLabel}>Dosage *</label>
                          <input 
                            type="text" 
                            value={medication.dosage} 
                            onChange={(e) => handleMedicationChange(index, 'dosage', e.target.value)} 
                            className={styles.formInput} 
                            placeholder="e.g., 500mg, 2 tablets"
                            autoComplete="off" 
                            required
                          />
                        </div>
                        <div className={styles.formGroup}>
                          <label className={styles.formLabel}>Quantity</label>
                          <input 
                            type="number" 
                            value={medication.quantity} 
                            onChange={(e) => handleMedicationChange(index, 'quantity', e.target.value)} 
                            className={styles.formInput} 
                            placeholder="Enter quantity prescribed"
                            autoComplete="off"
                            min="0"
                            step="1"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <button 
                  type="button" 
                  onClick={addMedication}
                  className={styles.addMedicationButton}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5v14m-7-7h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  Add Another Medication
                </button>
              </div>

              {/* Additional Information */}
              <div className={styles.medicalFieldsGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Diagnostics</label>
                  <textarea 
                    name="diagnostics" 
                    value={formData.diagnostics} 
                    onChange={handleInputChange} 
                    className={styles.formTextarea} 
                    placeholder="Enter diagnostic information and findings..."
                    rows="4"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Referral</label>
                  <textarea 
                    name="referral" 
                    value={formData.referral} 
                    onChange={handleInputChange} 
                    className={styles.formTextarea} 
                    placeholder="Enter referral information or recommendations..."
                    rows="3"
                  />
                </div>
              </div>
              
              {/* Form Buttons - Removed per user request */}
            </form>

            {/* Success Message */}
            {submitSuccess && (
              <div className={styles.successMessage}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <p>Medication information saved successfully!</p>
              </div>
            )}
          </div>
        </div>
        </div>
      </main>
    </div>
  );
}

export default ConsultMedication;
